import { useRef } from 'react'

/**
 * useDragDismiss — pull-a-sheet-down-to-close as a reusable building block.
 *
 * Moved VERBATIM from EntdeckenOverlay (Issue #299 Etappe 4); the identical
 * copy in TschaukoVoiceOverlay ("same pattern as EntdeckenOverlay") now uses
 * this hook too. Behavior unchanged: downward-only drag with velocity
 * tracking, dismiss on a long pull (>110 px) OR a fast downward flick
 * (>0.6 px/ms), otherwise spring back open.
 *
 * NOT for snap-point sheets — that's the separate BottomSheet building block
 * (collapsed/half/expanded). This hook is for open/closed-only overlays.
 *
 * Usage: spread `handleProps` onto the drag-handle element and put
 * `sheetRef` on the sliding sheet element.
 */
export function useDragDismiss(onDismiss: () => void): {
  sheetRef: React.RefObject<HTMLDivElement | null>
  handleProps: {
    onPointerDown: (e: React.PointerEvent) => void
    onPointerMove: (e: React.PointerEvent) => void
    onPointerUp: (e: React.PointerEvent) => void
    onPointerCancel: (e: React.PointerEvent) => void
  }
} {
  const sheetRef = useRef<HTMLDivElement>(null)
  const drag = useRef({ active: false, startY: 0, dy: 0, lastY: 0, lastT: 0, vy: 0 })

  const moveSheet = (y: number) => {
    const el = sheetRef.current
    if (el) el.style.transform = y > 0 ? `translateY(${y}px)` : ''
  }

  const onPointerDown = (e: React.PointerEvent) => {
    const el = sheetRef.current
    if (!el) return
    drag.current = { active: true, startY: e.clientY, dy: 0, lastY: e.clientY, lastT: performance.now(), vy: 0 }
    el.style.transition = 'none'
    try { (e.currentTarget as HTMLElement).setPointerCapture(e.pointerId) } catch { /* unsupported */ }
  }

  const onPointerMove = (e: React.PointerEvent) => {
    const d = drag.current
    if (!d.active) return
    const dy = Math.max(0, e.clientY - d.startY) // downward only — there is no half-open state
    const now = performance.now()
    d.vy = (e.clientY - d.lastY) / Math.max(1, now - d.lastT) // px/ms
    d.lastY = e.clientY
    d.lastT = now
    d.dy = dy
    moveSheet(dy)
  }

  const settle = (close: boolean) => {
    const el = sheetRef.current
    if (!el) { if (close) onDismiss(); return }
    el.style.transition = 'transform 0.28s cubic-bezier(0.32, 0.72, 0, 1)'
    if (close) {
      el.style.transform = 'translateY(110%)'   // slide the rest of the way down, then unmount
      window.setTimeout(onDismiss, 250)
    } else {
      el.style.transform = ''                    // spring back open
    }
  }

  const onPointerUp = (e: React.PointerEvent) => {
    const d = drag.current
    if (!d.active) return
    d.active = false
    try { (e.currentTarget as HTMLElement).releasePointerCapture(e.pointerId) } catch { /* noop */ }
    // Dismiss on a long pull OR a fast downward flick; otherwise spring back.
    settle(d.dy > 110 || d.vy > 0.6)
  }

  return {
    sheetRef,
    handleProps: { onPointerDown, onPointerMove, onPointerUp, onPointerCancel: onPointerUp },
  }
}
